import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { ArrowLeftIcon, XMarkIcon, DocumentIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import RichTextEditor from '../../components/RichTextEditor';
import DateTimePickerInput from '../../components/DateTimePickerInput';
import { uploadAttachmentsDirect } from '../../utils/directAttachmentUpload';

// Hardcoded knowledge article categories
const KNOWLEDGE_CATEGORIES = [
  { value: 'guideline', label: 'Guideline' },
  { value: 'known_error', label: 'Known Error' },
  { value: 'kb_document', label: 'KB Document' },
  { value: 'domain_controller_build', label: 'Domain Controller Build' },
  { value: 'mfa_modification', label: 'MFA Modification' },
  { value: 'investigation_steps', label: 'Investigation steps' },
  { value: 'itsm_l2_sop', label: 'ITSM - L2 SOP For Attaching and Initiali' },
  { value: 'sending_encrypted_email', label: 'Sending Encrypted Email' },
  { value: 'windows_updates', label: 'Windows Updates' },
  { value: 'fsmo_migration', label: 'FSMO Migration' }
];

interface KnowledgeFormData {
  title: string;
  shortDescription: string;
  content: string;
  category: string;
  knowledgeBaseId: string;
  kbGroupEmail: string;
  validTill: string;
  tags: string[];
  state: string;
  published: boolean;
  assignedTo?: string;
}

interface KnowledgeBase {
  id: string;
  name: string;
  active: boolean;
}

interface PendingFile {
  file: File;
  id: string;
}

const KnowledgeCreate: React.FC = () => {
  const navigate = useNavigate();
  const { can, loading: permissionsLoading } = useDynamicPermissions();
  const hasPermission = can('knowledge', 'create');
  const { user } = useAuth();

  const [formData, setFormData] = useState<KnowledgeFormData>({
    title: '',
    shortDescription: '',
    content: '',
    category: '',
    knowledgeBaseId: '',
    kbGroupEmail: '',
    validTill: '',
    tags: [],
    state: 'new',
    published: false
  });
  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState<Partial<Record<keyof KnowledgeFormData | 'submit' | 'attachments', string>>>({});

  // File upload states
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch knowledge bases for dropdown
  const { data: knowledgeBasesData } = useQuery({
    queryKey: ['knowledge-bases'],
    queryFn: async () => {
      const response = await api.get('/knowledge-base?limit=100&active=true');
      return response.data;
    }
  });

  const knowledgeBases: KnowledgeBase[] = knowledgeBasesData?.knowledgeBases || [];

  // Get current user's display name
  const authorName = user ? `${(user as any).firstName || ''} ${(user as any).lastName || ''}`.trim() || (user as any).email : '';

  const createMutation = useMutation({
    mutationFn: async (data: KnowledgeFormData) => {
      const payload: any = {
        ...data,
        shortDescription: data.title, // Use title as short description
        published: data.state === 'published',
        assignedTo: (user as any)?.id
      };
      // Only include kbGroupEmail if it has a value
      if (!data.kbGroupEmail) {
        delete payload.kbGroupEmail;
      }
      const response = await api.post('/knowledge', payload);
      return response.data;
    },
    onSuccess: async (data) => {
      const articleId = data._id || data.id;

      // Upload pending files if any
      if (pendingFiles.length > 0) {
        setIsUploading(true);
        try {
          await uploadAttachmentsDirect(
            api,
            `/knowledge/${articleId}/attachments`,
            pendingFiles.map((pendingFile) => pendingFile.file)
          );
          toast.success(`Article created with ${pendingFiles.length} attachment(s)`);
        } catch (uploadError: any) {
          toast.error(`Article created but failed to upload attachments: ${uploadError.response?.data?.error || 'Unknown error'}`);
        } finally {
          setIsUploading(false);
        }
      } else {
        toast.success('Article created successfully');
      }

      navigate(`/knowledge/${articleId}`);
    },
    onError: (error: any) => {
      if (error.response?.data?.errors) {
        const validationErrors: Partial<Record<keyof KnowledgeFormData, string>> = {};
        error.response.data.errors.forEach((err: any) => {
          if (err.field) {
            validationErrors[err.field as keyof KnowledgeFormData] = err.message;
          }
        });
        setErrors(validationErrors);
      } else {
        setErrors({ submit: error.response?.data?.message || 'Failed to create article' });
      }
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate form
    const newErrors: Partial<Record<keyof KnowledgeFormData | 'attachments', string>> = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    if (!formData.knowledgeBaseId) {
      newErrors.knowledgeBaseId = 'Knowledge Base is required';
    }
    // Validate email if provided
    if (formData.kbGroupEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.kbGroupEmail)) {
      newErrors.kbGroupEmail = 'Please enter a valid email address';
    }
    if (pendingFiles.length === 0) {
      newErrors.attachments = 'At least one attachment is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    createMutation.mutate(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Inline email validation for kbGroupEmail
    if (name === 'kbGroupEmail') {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setErrors(prev => ({ ...prev, kbGroupEmail: 'Please enter a valid email address' }));
      } else {
        setErrors(prev => ({ ...prev, kbGroupEmail: undefined }));
      }
      return;
    }

    // Clear error for this field
    if (errors[name as keyof KnowledgeFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleContentChange = (value: string) => {
    setFormData(prev => ({ ...prev, content: value }));
    if (errors.content) {
      setErrors(prev => ({ ...prev, content: undefined }));
    }
  };

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const tag = tagInput.trim();
      if (tag && !formData.tags.includes(tag)) {
        setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
        setTagInput('');
      }
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // File upload helpers
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newPendingFiles: PendingFile[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      // Check file size (25 MB limit)
      if (file.size > 25 * 1024 * 1024) {
        toast.error(`File "${file.name}" exceeds 25 MB limit`);
        continue;
      }
      newPendingFiles.push({
        file,
        id: `${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}`
      });
    }

    if (newPendingFiles.length > 0) {
      setPendingFiles(prev => [...prev, ...newPendingFiles]);
      // Clear attachment error when files are added
      if (errors.attachments) {
        setErrors(prev => ({ ...prev, attachments: undefined }));
      }
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemovePendingFile = (fileId: string) => {
    setPendingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // Show loading while checking permissions
  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check permission
  if (!hasPermission) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          You don't have permission to create knowledge articles.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-8 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/knowledge')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Knowledge Articles
          </button>
        </div>

        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h1 className="text-xl font-semibold text-gray-900">Create Knowledge Article</h1>
            <p className="text-sm text-gray-600 mt-1">
              Create a new knowledge article to share information with users
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-8">
            {errors.submit && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">{errors.submit}</p>
              </div>
            )}

            {/* Row 1: Title (full width) */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-1">
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 pt-2">
                      Title <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-11">
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Enter article title"
                      className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.title ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                      }`}
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Category & State */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                      Category <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.category ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select Category</option>
                      {KNOWLEDGE_CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                    {errors.category && (
                      <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                      State
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value="Draft"
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: Knowledge Base & KB Group Email */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <label htmlFor="knowledgeBaseId" className="block text-sm font-medium text-gray-700">
                      Knowledge Base <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="knowledgeBaseId"
                      name="knowledgeBaseId"
                      value={formData.knowledgeBaseId}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.knowledgeBaseId ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select Knowledge Base</option>
                      {knowledgeBases.map(kb => (
                        <option key={kb.id} value={kb.id}>{kb.name}</option>
                      ))}
                    </select>
                    {errors.knowledgeBaseId && (
                      <p className="mt-1 text-sm text-red-600">{errors.knowledgeBaseId}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <label htmlFor="kbGroupEmail" className="block text-sm font-medium text-gray-700">
                      KB Group Email
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="email"
                      id="kbGroupEmail"
                      name="kbGroupEmail"
                      value={formData.kbGroupEmail}
                      onChange={handleChange}
                      placeholder="Enter group email (optional)"
                      className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.kbGroupEmail ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                      }`}
                    />
                    {errors.kbGroupEmail && (
                      <p className="mt-1 text-sm text-red-600">{errors.kbGroupEmail}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 4: Author & Valid Till */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <label htmlFor="author" className="block text-sm font-medium text-gray-700">
                      Author
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      id="author"
                      value={authorName}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <label htmlFor="validTill" className="block text-sm font-medium text-gray-700">
                      Valid Till
                    </label>
                  </div>
                  <div className="col-span-9">
                    <DateTimePickerInput
                      id="validTill"
                      name="validTill"
                      value={formData.validTill}
                      onChange={(val) => setFormData(prev => ({ ...prev, validTill: val }))}
                      showTimeSelect={false}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 5: Attachments */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 pt-2">
                      Attachments <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-11">
                    <div className={`border-2 border-dashed rounded-lg p-4 text-center ${
                      errors.attachments ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}>
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        id="attachments"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.txt,.xlsx,.xls,.csv"
                      />
                      <label
                        htmlFor="attachments"
                        className="cursor-pointer text-sm text-gray-600 hover:text-blue-600"
                      >
                        <span className="text-blue-600 hover:text-blue-700">Click to upload</span> or drag and drop
                        <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX, PNG, JPG up to 25MB</p>
                      </label>
                    </div>
                    {errors.attachments && (
                      <p className="mt-1 text-sm text-red-600">{errors.attachments}</p>
                    )}

                    {/* Pending files list */}
                    {pendingFiles.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="text-sm font-medium text-gray-700">Files to upload ({pendingFiles.length})</p>
                        <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                          {pendingFiles.map((pendingFile) => (
                            <div key={pendingFile.id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <DocumentIcon className="h-8 w-8 text-gray-400 flex-shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{pendingFile.file.name}</p>
                                  <p className="text-xs text-gray-500">
                                    {formatFileSize(pendingFile.file.size)}
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemovePendingFile(pendingFile.id)}
                                className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-gray-100"
                                title="Remove"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 6: Keywords/Tags */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 pt-2">
                      Keywords
                    </label>
                  </div>
                  <div className="col-span-11">
                    <div className="flex flex-wrap gap-2 mb-2">
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleAddTag}
                      placeholder="Type a keyword and press Enter"
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Press Enter or comma to add a keyword
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 7: Content (Rich Text) */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 pt-2">
                      Content
                    </label>
                  </div>
                  <div className="col-span-11">
                    <RichTextEditor
                      value={formData.content}
                      onChange={handleContentChange}
                      placeholder="Write your article content here..."
                      minHeight="250px"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/knowledge')}
                disabled={createMutation.isPending || isUploading}
                className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || isUploading}
                className="px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isUploading ? 'Uploading files...' : createMutation.isPending ? 'Creating...' : 'Save as Draft'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeCreate;
